<template lang="pug">
	.easy-creative--question.question.flex-col.space.max-limited(:class="{ 'highlighted-error': !!error }", :style="questionWidthStyle")
		.flex-row.justify.space
			.label(v-if="questionType === 'info'", :style="{ 'font-size': question.FontSize ? `${question.FontSize}pt` : null }") {{question.Question}}
			.flex-row.space.align-center(v-else-if="!['tabs'].includes(questionType)")
				.flex-row.space.align-center
					.font-big(:class="{ 'highlighted-error': !!error }") {{question.Question}}
					Loader(v-if="isQuestionLoading", text="")
				.font-tiny.grey(v-if="question.IsRequired") ({{$tCaption('required')}})
			.flex-row.space.align-center
				template(v-if="question.Hint")
					.hint.clickable.hoverable(:id="`hint_${uid}`")
						fas(icon="circle-question", @mouseenter="isTooltipVisible = true", @mouseleave="isTooltipVisible = false")
					DxPopover(:visible="isTooltipVisible" :hide-on-outside-click="false" :target="`#hint_${uid}`", :maxWidth="500")
						span {{question.Hint}}
		.error.font-tiny.upper(v-if="error", :class="{ 'highlighted-error': !!error }") {{error}} 
		DxTextArea(v-if="questionType === 'text' && question.TextEditorType === 0", :value.sync="question.Answer", stylingMode="filled", :placeholder="question.Placeholder || $tCaption('Write something here..')", :height="100")
		DxTextBox(v-if="questionType === 'text' && question.TextEditorType === 1", :value.sync="question.Answer", stylingMode="filled", :placeholder="question.Placeholder || $tCaption('Write something here..')")
		template(v-if="questionType === 'websiteUrl' && isClientRequest")
			.flex-col.space
				DxTextBox.flex-1(:value.sync="websiteUrl", stylingMode="filled", :placeholder="question.Placeholder || $tCaption('Write something here..')", :disabled="!!isQuestionLoading")
		DxTextBox.flex-1(v-if="questionType === 'websiteUrl' && !isClientRequest", :value.sync="question.Answer", valueChangeEvent="input", stylingMode="filled", :placeholder="question.Placeholder || $tCaption('Write something here..')")
		.flex-row.space.logical(v-if="questionType === 'logical'", :value.sync="question.Answer")
			DxButton(@click="logicalAnswer = true", :text="$tCaption('Yes')", :type="logicalAnswer === true ? 'default' : 'normal'", :activeStateEnabled="false", :focusStateEnabled="false")
			DxButton(@click="logicalAnswer = false", :text="$tCaption('No')", :type="logicalAnswer === false ? 'default' : 'normal'", :activeStateEnabled="false", :focusStateEnabled="false")
		DxCalendar(v-if="questionType === 'date'", :value.sync="dateValue", stylingMode="filled")
		DxDateBox(v-if="questionType === 'time'", :value.sync="timeValue", type="time", pickerType="native", stylingMode="filled")
		DxNumberBox(v-if="questionType === 'number'", :value.sync="question.NumberAnswer", stylingMode="filled", :showSpinButtons="true", :placeholder="question.Placeholder",)
		DxNumberBox(v-if="questionType === 'currency'", :value.sync="question.CurrencyAnswer", stylingMode="filled", :showSpinButtons="true", :placeholder="question.Placeholder",)
		template(v-if="questionType === 'list' && question.BriefPickListItems")
			EasyCreativeQuestionHierarchical(v-if="question.__isHierarchical", v-bind="$props")
			DxSelectBox(v-else, :data-source="question.BriefPickListItems", :placeholder="question.Placeholder", :search-enabled="true", :showClearButton="true",
				:value.sync="question.PickListAnswerId", stylingMode="filled", display-expr="Name", value-expr="Oid", @item-click="e => $set(question, 'BriefPickListDisplayValue', e.itemData.Name)")
		.flex-col.space(v-if="questionType === 'file' || questionType === 'audioToText'")
			.flex-row.space.align-center
				.flex-row.space.align-center(v-if="fileAnswerName", :class="{ 'disabled-controls': isQuestionLoading }")
					.font-lg {{fileAnswerName}}
					DxButton(icon="close", @click="removeFile")
				FileUploader(v-else, :multiple="fileUploadMultiple", :text="fileAnswerName ? '' : $tCaption('Upload file')", :icon="fileAnswerName ? 'refresh' : 'upload'", :disabled="!!isLoading || !!isQuestionLoading", @upload="onFileUpload", 
					@popup="onFileUploadPopup", @file-selected="fileSelected = true", :accept="fileUploadAccept")
					.flex-col.space-medium(v-if="questionType === 'audioToText' && fileSelected")
						.flex-col
							.font-small.upper.grey {{$tCaption('Language')}}
							DxSelectBox(:data-source="languages", :placeholder="getLanguagePlaceholder(audioToText.language)", :search-enabled="true", :showClearButton="true", @focusIn="selectOnFocus"
								:value="getLanguageValue(audioToText.language)", stylingMode="filled", display-expr="text", value-expr="text", @item-click="e => audioToText.language = e.itemData",
								@selection-changed="e => { if (!e.selectedItem) { audioToText.language = info.language() } }")
						.flex-col(v-if="translateSupported")
							.font-small.upper.grey {{$tCaption('Translate to')}}
							DxSelectBox(:data-source="languages", :placeholder="getLanguagePlaceholder(audioToText.translateTo)", :search-enabled="true", :showClearButton="true", @focusIn="selectOnFocus"
								:value="getLanguageValue(audioToText.translateTo)", stylingMode="filled", display-expr="text", value-expr="text", @item-click="e => audioToText.translateTo = e.itemData",
								@selection-changed="e => { if (!e.selectedItem) { audioToText.translateTo = info.language() } }")
				Loader(v-if="isLoading", text="")
			template(v-if="questionType === 'audioToText'")
				DxTextArea(stylingMode="filled", :value.sync="question.Answer", v-if="audioToText.processed")
			.flex-col.space(v-if="question.Answer && questionType === 'file' && question.FileEditorType === 1")
				.flex-row.space.wrap
					img.thmb-uploaded-image(v-for="thmb in fileUploadThumbnails" :src="thmb")
				pre {{question.Answer}}
		.slider.flex-row.align-center(v-if="questionType === 'range'")
			EditableLabel(:useCustomStyles="true")
				.range-value {{rangeValue[0]}}
				template(slot="editor")
					DxNumberBox.text-center(:value="rangeValue[0]", @value-changed="e => applyRangeValue([e.value, rangeValue[1]])", :width="70", stylingMode="filled", :min="question.MinValue || 0", :max="question.MaxValue || 100")
			DxRangeSlider.flex-1(ref="rangeSlider", :min="question.MinValue || 0", :max="question.MaxValue || 100", :value.sync="rangeValue", :step="rangeStep")
			EditableLabel(:useCustomStyles="true")
				.range-value {{rangeValue[1]}}
				template(slot="editor")
					DxNumberBox.text-center(:value="rangeValue[1]", @value-changed="e => applyRangeValue([rangeValue[0], e.value])", :width="70", stylingMode="filled", :min="question.MinValue || 0", :max="question.MaxValue || 100")
		.flex-row.space(v-if="questionType === 'email'")
			.flex-col.flex-1
				.font-small {{$tCaption('Name')}}
				DxTextBox(:value.sync="emailName", :placeholder="$tCaption('Name')", stylingMode="filled")
			.flex-col.flex-1
				.font-small {{$tCaption('Email')}}
				DxTextBox(mode="email", :value.sync="email", :placeholder="$tCaption('Email')", stylingMode="filled")
		.flex-col.space-medium(v-if="questionType === 'keywords'")
			.flex-col.flex-1
				.font-small {{$tCaption('URL')}}
				.flex-row.space.align-center
					span {{$tCaption('http(s)://')}}
					DxTextBox(:value.sync="url", :placeholder="$tCaption('URL')", stylingMode="filled", @value-changed="loadKeywords")
			.flex-col.flex-1(v-if="isKeywordsLoaded || (keywords && keywords.length)")
				.font-small {{$tCaption('Keywords')}}
				.flex-row.space.align-center
					DxTagBox.flex-1(
						:items="loadedKeywords",
						:accept-custom-value="true",
						:search-enabled="true",
						:value="keywords",
						:show-selection-controls="true",
						:disabled="!!isQuestionLoading",
						@value-changed="v => keywords = v.value")
						DxTagBoxButton(v-if="keywords && keywords.length", name="clearAll", :options="{text: '', icon: 'clear', type: 'default', onClick: () => keywords = []}")
		DxSelectBox(v-if="questionType === 'language'", :data-source="info.filteredLanguages", :placeholder="languagePlaceholder", :search-enabled="true", :showClearButton="true", @focusIn="selectOnFocus"
			:value="languageValue", stylingMode="filled", display-expr="text", value-expr="text", @item-click="e => setLanguage(e.itemData)", @selection-changed="e => { if (!e.selectedItem) { setLanguage(null); } }")
		DxRadioGroup(v-if="questionType === 'radioGroup'", :dataSource="question.BriefPickListItems", display-expr="Name", value-expr="Oid", :value.sync="question.PickListAnswerId")
		.flex-col.space-medium(v-if="questionType === 'productName'")
			.flex-row.space.align-center
				DxTextBox.flex-1(:value.sync="productNameAnswer.product", valueChangeEvent="input", stylingMode="filled", :placeholder="question.Placeholder || $tCaption('Write something here..')", :disabled="!!isQuestionLoading")
			pre.product-reviews(v-if="productNameAnswer.result") {{ productNameAnswer.result }}
		EasyCreativeTabs(v-if="questionType === 'tabs'", :info="info", :question="question", :requiredExists="requiredExists", :questionTypes="questionTypes")
</template>

<script>
	import _ from 'lodash';
	import Loader from 'app-core/Loader.vue';
	import DxButton from 'devextreme-vue/button';
	import DxTextArea from 'devextreme-vue/text-area';
	import DxCalendar from 'devextreme-vue/calendar';
	import DxNumberBox from 'devextreme-vue/number-box';
	import DxDateBox from 'devextreme-vue/date-box';
	import DxSelectBox from 'devextreme-vue/select-box';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxRangeSlider from 'devextreme-vue/range-slider';
	import { DxPopover } from 'devextreme-vue/popover';
	import FileUploader from 'app-core/FileUploader.vue';
	import EasyCreativeQuestionHierarchical from './EasyCreativeQuestionHierarchical.vue';
	import cognitiveArrays from 'view/insertion/cognitive-arrays';
	import DxRadioGroup from 'devextreme-vue/radio-group';
	import EditableLabel from 'app-core/EditableLabel.vue';
	import { DxTagBox, DxButton as DxTagBoxButton  } from 'devextreme-vue/tag-box';
	import { uploadBlobWithInfoResponse, deleteBlob } from 'app-core/azure';
	import EasyCreativeTabs from './EasyCreativeTabs.vue';

	export default {
		translationCategory: 'creative.EasyCreative',
		name: 'easy-creative-question',
		props: ['info', 'question', 'questionTypes', 'error', 'requiredExists'],
		mixins: [cognitiveArrays],
		components: {
			Loader, DxButton, DxTextArea, DxCalendar, DxNumberBox, DxDateBox, DxSelectBox, DxTextBox, DxPopover, DxRangeSlider, FileUploader, DxRadioGroup,
			EasyCreativeQuestionHierarchical, EditableLabel, DxTagBox, DxTagBoxButton, EasyCreativeTabs
		},
		data() {
			return {
				logicalAnswer: this.question.__LogicalAnswer,
				isTooltipVisible: false,
				isLoading: false,
				defaultLanguage: null,
				audioToText: {
					language: null,
					translateTo: null,
					processed: false
				},
				fileSelected: false,
				loadedKeywords: [],
				isQuestionLoading: 0,
				isKeywordsLoaded: false,
				scrappingInitialized: false,
				fileUploadThumbnails: []
			};
		},
		computed: {
			isClientRequest() {
				return this.question.ClientRequest;
			},
			questionWidthStyle() {
				const style = {};

				if (this.question.Width) {
					if (!isNaN(this.question.Width)) {
						style.flex = this.question.Width;
					} else {
						style.width = this.question.Width;
					}
				}

				return style;
			},
			uid() {
				return `question_${this.question.Oid}`;
			},
			questionType() {
				return this.questionTypes[this.question?.BriefQuestionType];
			},
			dateValue: {
				get: function() {
					return this.question.DateAnswer === "0001-01-01T00:00:00" || !this.question.DateAnswer ? null : new Date(this.question.DateAnswer);
				},
				set: function(v) {
					this.question.DateAnswer = this.$Data.dateToString(v);
				}
			},
			timeValue: {
				get: function() {
					return this.$moment(this.question.TimeAnswer, ['h:m:s a', 'H:m:s']).toDate();
				},
				set: function(v) {
					this.question.TimeAnswer = this.$moment(v).format('H:mm:ss');
				}
			},
			rangeValue: {
				get: function() {
					if (!this.question.RangeAnswer) {
						this.$set(this.question, 'RangeAnswer', [this.question.MinValue || 0, this.question.MaxValue || 100]);
					}
					return this.question.RangeAnswer;
				},
				set: function(v) {
					this.$set(this.question, 'RangeAnswer', v);
				}
			},
			rangeStep() {
				const v = (this.question.MaxValue - this.question.MinValue);
				if (v <= 1) return 0.01;
				return 1;
			},
			fileAnswerName() {
				return this.question?.__fileName;
			},
			email: {
				get: function() { return this.question?.Answer?.email; },
				set: function(v) { 
					this.addObjectAnswerValues({ email: v });
				}
			},
			emailName: {
				get: function() { return this.question?.Answer?.name; },
				set: function(v) { 
					this.addObjectAnswerValues({ name: v });
				}
			},
			url: {
				get: function() { return this.question?.Answer?.url; },
				set: function(v) { 
					this.addObjectAnswerValues({ url: v });
				}
			},
			keywords: {
				get: function() { return this.question?.Answer?.keywords; },
				set: function(v) { 
					this.addObjectAnswerValues({ keywords: v });
				}
			},
			languagePlaceholder() {
				return this.question.Answer?.locale === this.defaultLanguage?.locale ? this.question.Answer?.text : this.question.Placeholder;
			},
			languageValue() {
				return this.question.Answer?.text;
			},
			translateSupported() {
				return false;
			},
			websiteUrl: {
				get: function() {
					return this.websiteUrlAnswer?.websiteUrl || '';
				},
				set: function(v) {
					this.websiteUrlAnswer.websiteUrl = v;
					this.doScrapWebsite();
				}
			},
			websiteUrlAnswer() {
				if (this.questionType === 'websiteUrl' && !this.question.Answer?.websiteUrl) {
					this.$set(this.question, 'Answer', {
						websiteUrl: null,
						info: null
					});
				}
				return this.question.Answer;
			},
			fileUploadAccept() {
				return this.question.FileEditorType === 1 ? ['.png', '.bmp', '.jpg', '.jpeg'] : null;
			},
			fileUploadMultiple() {
				return this.question.FileEditorType === 1;
			},
			productNameAnswer() {
				if (this.questionType === 'productName' && !this.question.Answer) {
					this.$set(this.question, 'Answer', {
						product: null,
						result: null
					});
				}
				return this.question.Answer;
			},
		},
		watch: {
			logicalAnswer(v) {
				this.question.LogicalAnswer = v;
				this.$set(this.question, '__LogicalAnswer', v);
			},
			questionType() {
				this.setDefaults();
			},
			question: {
				handler() {
					if (!this.question?.__displayValue) {
						this.setDefaults();
					}

					const values = {
						displayValue: this.question?.__displayValue,
						value: this.question?.__value
					};

					if (JSON.stringify(this.question.__values) != JSON.stringify(values)) {
						this.$set(this.question, '__values', values);
						this.replaceSimilarQuestions();
					}
				},
				deep: true
			},
			'question.PickListAnswerId'(v) {
				const item = this.question.BriefPickListItems?.find(f => f.Oid === v);
				if (item) {
					this.$set(this.question, 'BriefPickListDisplayValue', item.Name);
				}
			},
			'productNameAnswer.product'(v) {
				if (v) {
					this.$processProductNameAnswer();
				}
			}
		},
		mounted() {
			if (__DEBUG__) {
				window.easyCreativeQuestion = this;
			}

			this.setDefaults();

			this.$processProductNameAnswer = _.debounce(this.processProductNameAnswer, 2000);
		},
		methods: {
			async processProductNameAnswer() {
				this.isQuestionLoading = true;

				let scrapeReviews = (await this.$Data.postExternalApi('OpenAiGpt3/Generate', {
					promptCode: 'SCRAPEREVIEWS',
					product: this.productNameAnswer.product,
					clientIp: this.$auth.userIP()
				}).finally(() => {
					this.isQuestionLoading = false;
				}))?.Data?.Content;

				if (__DEBUG__)
					console.debug('Scrap reviews', { scrapeReviews });

				this.productNameAnswer.result = scrapeReviews;
			},
			async calcFileUploadThumbnails() {
				if (this.question.FileEditorType !== 1) return [];

				return this.question.__files?.length ? await Promise.all(this.question.__files.map(f => this.$Data.blob2Base64(f))) : [];
			},
			async doScrapWebsite() {
				if (!this.websiteUrl) return;

				this.isQuestionLoading = true;

				let scrapSummary = (await this.$Data.postExternalApi('OpenAiGpt3/Generate', {
					promptCode: 'WEBSITESUMMARY',
					url: `http://${(this.websiteUrl || '').replace(/http(s)?:\/\//g, '')}`,
					clientIp: this.$auth.userIP()
				}).finally(() => {
					this.isQuestionLoading = false;
					this.scrappingInitialized = true;
				}))?.Data?.Content;

				if (__DEBUG__)
					console.debug('Scrap website', { scrapSummary });

				this.websiteUrlAnswer.info = scrapSummary;
			},
			loadKeywords() {
				if (!this.url) return Promise.resolve(null);
				this.isQuestionLoading++;
				return this.$Data.getExternalApi('CloudApiKeywords/getDomainKeywords', `domain=${this.url}`).then(r => {
					if (__DEBUG__)
						console.log('getDomainKeywords loaded', r);
					if (this._isDestroyed) return;

					this.loadedKeywords = r.sort((a,b) => a.Position - b.Position).map(f => f.Term);
					this.keywords = this.loadedKeywords.slice(0, 10);
				}).finally(() => {
					this.isQuestionLoading--;
					this.isKeywordsLoaded = true;
				});
			},
			applyRangeValue(val) {
				if (this.$refs.rangeSlider?.instance) {
					this.$refs.rangeSlider.instance.option('value', val);
				}
			},
			replaceSimilarQuestions() {
				if (!this.question.__displayValue) return;
				const questions = this.info.questions();
				const questionItems = questions.allQuestions.filter(f => f.SimilarQuestion?.Oid === this.question?.Oid);
				questionItems.map(f => {
					const value = this.question.__value != null && typeof(this.question.__value) === 'object' ? JSON.stringify(this.question.__value) : this.question.__value;
					questions.setQuestionValueByAnswer(f, {
						Value: questions.isSameQuestionTypeGroup(this.question, f.__type) ? value : null,
						DisplayValue: this.question.__displayValue
					});
				});
			},
			onFileUploadPopup() {
				this.fileSelected = false;

				if (this.questionType === 'audioToText') {
					if (!this.audioToText.language) this.audioToText.language = this.info.language();
					if (!this.audioToText.translateTo) this.audioToText.translateTo = this.info.language();
				}
			},
			getLanguagePlaceholder(lang, def) {
				const defLang = this.info.language();
				return lang?.locale === defLang?.locale ? defLang?.text : def;
			},
			getLanguageValue(lang) {
				const defLang = this.info.language();
				return lang?.locale === defLang?.locale ? null : lang?.text;
			},
			addObjectAnswerValues(obj) {
				this.$set(this.question, 'Answer', { ...(this.question?.Answer || {}), ...(obj || {}) });
			},
			async onFileUpload(files) {
				const file = files && files[0];
				if (!file) return;

				this.$set(this.question, 'FileAnswer', null);
				this.$set(this.question, '__file', file);
				this.$set(this.question, '__fileName', file.name);

				if (__DEBUG__)
					console.debug('OnFileUpload', { file, qt: this.questionType, ed: this.question.FileEditorType });

				if (this.questionType === 'audioToText') {
					const base64 = await this.$Data.blob2Base64(file);
					const base64Info = this.$Data.splitDataUri(base64);
					const ext = this.$Data.getBase64AudioExtension(base64 || '');
					this.isLoading++;
					this.audioToText.processed = false;
					const processingResult = await this.$Data.postExternalApi('SpeechProcessingProfile/ProcessAudio', {
						ProfileCode: 'BriefAudioProcessing',
						Language: this.audioToText.language?.locale === this.audioToText.translateTo?.locale ? null : this.audioToText.language?.locale,
						TranslateTo: !this.translateSupported || this.audioToText.language?.locale === this.audioToText.translateTo?.locale ? null : this.audioToText.translateTo?.locale,
						FileExtension: ext,
						NeedValidateAudio: true,
						Base64StringData: base64Info.base64,
					}).finally(() => {
						this.isLoading--;
						this.audioToText.processed++;
					});
					let text = (typeof(processingResult) === 'string') ? processingResult : processingResult?.FinalText;
					this.$set(this.question, 'Answer', text);
				}

				if (this.questionType === 'file' && this.question.FileEditorType === 1 && files?.length) {
					this.$set(this.question, '__files', files);
					this.$set(this.question, '__fileName', this.$tCaption('Image(s)'));
					this.calcFileUploadThumbnails().then(r => this.fileUploadThumbnails = r);
					this.isQuestionLoading = true;
					try {
						let blobs = await Promise.all(files.map(async f => {
							const azureBlob = await uploadBlobWithInfoResponse(`brief_image_${this.$Data.uuid()}_${f.name}`, f, 'other');
							const azureUrl = azureBlob?.AzureFileUrl;

							return {
								azureUrl,
								azureBlob
							}
						}))

						try {
							let imageSummary = (await this.$Data.postExternalApi('OpenAiGpt3/Generate', {
								promptCode: 'IMAGEDESCRIPTION',
								attachments: blobs.map(f => f.azureUrl),
								clientIp: this.$auth.userIP()
							}))?.Data?.Content;

							this.$set(this.question, 'Answer', imageSummary);
						} finally {
							blobs.map(f => deleteBlob(f?.azureBlob?.FileName, f?.azureBlob?.AzureContainer));
						}
					} finally {
						this.isQuestionLoading = false;
					}
				}
			},
			removeFile() {
				this.$set(this.question, 'Answer', null);
				this.$set(this.question, 'FileAnswer', null);
				this.$set(this.question, '__file', null);
				this.$set(this.question, '__fileName', null);
			},
			async setDefaults() {
				if (__DEBUG__) {
					window[`easyCreativeQuestion_${this.questionType}`] = this;
				}
				this.logicalAnswer = this.question.__LogicalAnswer;
				if (this.questionType === 'language') {
					await this.loadSpeechVoices();
					if (this.info.brief?.DefaultLanguage?.Code && !this.defaultLanguage) {
						this.defaultLanguage = this.info.filteredLanguages.find((f) => f.locale === this.info.brief?.DefaultLanguage?.Code);
					}
					if (!this.defaultLanguage) {
						this.defaultLanguage = this.info.filteredLanguages.find((f) => f.locale === window.navigator.language);
					}
					if (!this.question.Answer) {
						if (this.defaultLanguage) {
							this.$set(this.question, '__defaultLanguage', this.defaultLanguage);
						}
						this.setLanguage(this.defaultLanguage);
					}
				}
			},
			setLanguage(lang) {
				if (__DEBUG__)
					console.debug('Set lang', { lang });
				
				this.$set(this.question, 'Answer', !lang ? _.pick(this.defaultLanguage, ['locale', 'text']) : _.pick(lang, ['locale', 'text']));
			},
			selectOnFocus(e) {
				if (__DEBUG__)
					console.debug('selectOnFocus', e);
				if (e?.element?.querySelector('input.dx-texteditor-input')?.select) {
					e.element.querySelector('input.dx-texteditor-input').select();
				}
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";
	.easy-creative--question {

		&.highlighted-error {
			border-left: 4px solid var(--branding-error-color);
			padding-left: 8px;
		}

		.highlighted-error {
			color: var(--branding-error-color);
		}

		.range-value {
			min-width: 40px;
			text-align: center;
			background: #f27649;
			color: #fff;
			border-radius: 40px;
			font-size: 12px;
			padding: 4px;

			&:hover {
				color: var(--branding-active-color) !important;
			}
		}

		.https-word {
			font-size: 15px;
			margin-top: 2px;
		}

		.thmb-uploaded-image {
			background: var(--img);
			background-size: cover;
			width: auto;
			max-height: 120px;
			border-radius: 4px;
		}

		pre {
			white-space: pre-wrap;
			font: inherit;
		}

		.product-reviews {
			font-size: 14px;
			font-weight: 100;
			font-style: italic;
		}
	}
</style>
